import React, { useMemo, useRef, useState } from 'react';
import { FileText, X, Download, Pencil, Check, Loader2, AlertTriangle } from 'lucide-react';
import jsPDF from 'jspdf';
import { Account } from '../../types';

/**
 * The customer's OTS application, in Marathi, as a PDF.
 *
 * This document is FROM THE CUSTOMER TO THE BANK. It carries no agent name,
 * no agency, no ScaleSupport, no logo, no internal identifier of any kind --
 * it has to read as an application the borrower wrote and signed themselves.
 * Anything identifying the recovery agent would make it something else.
 *
 * WHY THE PDF IS RENDERED AS AN IMAGE: jsPDF's built-in fonts are Latin-only.
 * Devanagari through jsPDF needs a Unicode font embedded as base64 -- roughly
 * a megabyte in the bundle, for one document. Instead the application is laid
 * out as HTML and rasterised with the browser's own Marathi font, which every
 * Windows and Android device already has. The text is therefore not selectable
 * in the PDF. For a printed application carried to a branch that costs
 * nothing, and it is the difference between correct Marathi and mojibake.
 *
 * Nothing is saved. The PDF is generated in the browser and handed straight to
 * the download, with no Firebase write and no Storage upload.
 */

export interface OTSApplicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The account the OTS belongs to. */
  account: Account | null;
  /** Every account covered by this OTS, including the one above. */
  relatedAccounts?: Account[];
  /** Which application to prepare. */
  stage: '10_percent_token' | 'full_settlement';
  /** The agreed total settlement figure. */
  agreedOtsAmount: number;
  /** What the customer is paying, or has paid, today. */
  paidAmount: number;
}

const inr = (n: number) =>
  (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

/** 28-09-2026 — the form Indian branches expect. */
function todayDMY(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
}

/** १, २, ३ … Devanagari numerals for the account list. */
const DEVA = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
const deva = (n: number) => String(n).split('').map((c) => DEVA[Number(c)] ?? c).join('');

interface LoanLine {
  accountNo: string;
  amount: string;
}


/**
 * THE EDITABLE FIELD, DECLARED ONCE.
 *
 * THE BUG THIS FIXES: this component used to be declared INSIDE the modal's
 * own render. React compares component types between renders, and a function
 * created afresh each time is a different type every time -- so on each
 * keystroke React threw the input away and built a new one. The new input had
 * no focus and no cursor, so typing a second character was impossible and
 * editing appeared to do nothing at all.
 *
 * Declared at module level it is the same component every render, and the
 * input keeps its focus.
 */
const Field: React.FC<{
  value: string;
  onChange: (v: string) => void;
  w?: string;
  editing: boolean;
}> = ({ value, onChange, w = 'auto', editing }) =>
  editing ? (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: w }}
      className="border-b border-dashed border-blue-400 bg-blue-50/40 px-1 outline-none"
    />
  ) : (
    <span>{value || '\u2014'}</span>
  );

export const OTSApplicationModal: React.FC<OTSApplicationModalProps> = ({
  isOpen,
  onClose,
  account,
  relatedAccounts,
  stage,
  agreedOtsAmount,
  paidAmount,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState('');

  const covered = useMemo(() => {
    const list = relatedAccounts && relatedAccounts.length > 0 ? relatedAccounts : account ? [account] : [];
    // No duplicates -- the same loan must not be listed twice in an
    // application that goes to a branch manager.
    const seen = new Set<string>();
    return list.filter((a) => {
      const key = (a.loanNumber || a.accountId || '').trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [account, relatedAccounts]);

  // Everything below is editable. The figures are seeded from the OTS record
  // so nothing has to be typed again, and corrected here if the record is
  // thin -- a missing district, an address recorded in short form.
  const [bankName, setBankName] = useState('');
  const [branch, setBranch] = useState('');
  const [zone, setZone] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [address, setAddress] = useState('');
  const [dateStr, setDateStr] = useState(todayDMY());
  const [loans, setLoans] = useState<LoanLine[]>([]);
  const [otsAmount, setOtsAmount] = useState('');
  const [paid, setPaid] = useState('');
  const [seededFor, setSeededFor] = useState('');

  // Seed when the window opens for a different account.
  const seedKey = `${account?.accountId || ''}|${stage}|${agreedOtsAmount}|${paidAmount}`;
  if (isOpen && account && seedKey !== seededFor) {
    setSeededFor(seedKey);
    setBankName((account as any).bankName || account.bank || '');
    setBranch(account.branch || '');
    /**
     * The bank's letter asks for the zone, not the district, and the account
     * already knows its zone. It used to be filled with the city as a stand-in
     * when no district was recorded, which meant an agent had to notice and
     * correct it. Blank is better than wrong: if the account has no zone the
     * line is left empty for the customer to complete by hand.
     */
    setZone((account as any).zone || (account as any).zonalOffice || '');
    setCustomerName(account.customerName || '');
    setAddress(account.address || '');
    setDateStr(todayDMY());
    setLoans(
      covered.map((a) => ({
        accountNo: a.loanNumber || a.accountId || '',
        amount: inr(a.outstandingAmount || a.overdueAmount || 0),
      }))
    );
    setOtsAmount(inr(agreedOtsAmount));
    setPaid(inr(paidAmount));
    setEditing(false);
    setFailed('');
  }

  if (!isOpen || !account) return null;

  const totalLoan = loans.reduce(
    (t, l) => t + (Number(String(l.amount).replace(/[^0-9.]/g, '')) || 0),
    0
  );
  const otsNum = Number(String(otsAmount).replace(/[^0-9.]/g, '')) || 0;
  const paidNum = Number(String(paid).replace(/[^0-9.]/g, '')) || 0;
  const remaining = Math.max(0, otsNum - paidNum);
  const isToken = stage === '10_percent_token';

  /**
   * THE PDF, MADE BY THE BROWSER ITSELF -- AND MADE TO FIT ONE PAGE.
   *
   * WHY NOT jsPDF: it cannot write Devanagari with its built-in fonts. The
   * browser already knows how to lay out Marathi and how to make a PDF, so
   * the application is opened in a clean window with an A4 page rule and the
   * print dialogue is called. "Save as PDF" gives a true A4 page with real,
   * selectable text, and the same button serves anyone printing on paper.
   *
   * WHY IT USED TO RUN TO TWO PAGES. The screen sheet carries its geometry as
   * INLINE styles -- width 190mm, padding 14mm 12mm, font-size 11.5pt,
   * line-height 1.85 -- because that is what makes it look like a sheet of
   * paper inside the modal. Those inline styles were copied into the print
   * window along with the markup, and an inline style beats any stylesheet
   * rule not marked important. So the print CSS was never actually in force:
   * the letter was laid out 190mm wide inside a body already padded 15mm a
   * side -- 220mm across a 210mm page -- at screen type size and screen
   * leading. It overflowed sideways and downwards, and the overflow became
   * page two. The `.ots-sheet` overrides meant to undo it never matched
   * anything either: that class is not on the div.
   *
   * WHAT IT DOES NOW. The sheet is cloned and the screen geometry stripped
   * off the clone, so the print stylesheet is the only thing sizing the
   * letter. Every measurement in that stylesheet is in `em`, so the whole
   * letter -- type, leading, paragraph gaps, indents -- scales from a single
   * font-size. The page then measures itself and steps that one size down
   * until the letter fits the printable height, so it lands on one page
   * whether the application covers one loan or six. Scaling the type rather
   * than transforming the page keeps the Marathi crisp and the text
   * selectable.
   */
  const download = () => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    setBusy(true);
    setFailed('');

    try {
      const w = window.open('', '_blank', 'width=900,height=1100');
      if (!w) {
        setFailed(
          'Your browser blocked the new window. Allow pop-ups for this site, then try again.'
        );
        setBusy(false);
        return;
      }

      const safeName = (customerName || 'application')
        .replace(/[^\wऀ-ॿ ]+/g, '')
        .trim();

      /* ---------- the clone that goes on paper ---------- */
      const clone = sheet.cloneNode(true) as HTMLElement;

      /* An input is a screen affordance. On paper the value is just text,
         underlined so the branch can see the line was filled in. This also
         means it no longer matters whether edit mode was left switched on. */
      Array.from(clone.querySelectorAll('input')).forEach((el) => {
        const span = document.createElement('span');
        span.className = 'ots-filled';
        span.textContent = (el as HTMLInputElement).value || '—';
        el.replaceWith(span);
      });

      /* The screen geometry, removed so the print stylesheet can do its job.
         `padding` is deliberately absent as a shorthand -- removing a
         shorthand removes its longhands with it, and the loan-amount indent
         is a padding-left. The longhands are listed instead, all but left. */
      const STRIP = [
        'width',
        'max-width',
        'min-width',
        'height',
        'max-height',
        'min-height',
        'font-size',
        'line-height',
        'font-family',
        'background',
        'background-color',
        'box-shadow',
        'border-radius',
        'overflow',
        'box-sizing',
        'margin',
        'margin-top',
        'margin-right',
        'margin-bottom',
        'padding-top',
        'padding-right',
        'padding-bottom',
      ];
      const scrub = (el: HTMLElement) => {
        if (el.style) STRIP.forEach((prop) => el.style.removeProperty(prop));
        Array.from(el.children).forEach((child) => scrub(child as HTMLElement));
      };
      scrub(clone);
      clone.setAttribute('class', 'ots-print-sheet');
      clone.removeAttribute('style');

      w.document.write(`<!doctype html>
<html lang="mr">
<head>
<meta charset="utf-8" />
<title>OTS अर्ज - ${safeName} - ${dateStr}</title>
<style>
  /* A4 with NO page margin, the white space supplied by the page box instead.

     That is not a stylistic choice. A browser prints its own header and
     footer -- the date and time, the page title, "about:blank", the page
     number -- into the page margin, and there is no CSS that switches them
     off. Setting the page margin to zero leaves them nowhere to go, so they
     disappear, and the margin the letter needs comes from #ots-page. */
  @page { size: A4 portrait; margin: 0; }

  html, body { margin: 0; padding: 0; background: #fff; color: #000; }

  /* THE PAGE BOX. 296mm rather than a full 297mm: a hair of slack, because a
     box exactly as tall as the sheet can round up by a fraction of a pixel
     and spill one empty page out the back. overflow:hidden makes one page
     certain even in that case, and the fitting script below is what makes
     sure nothing is actually cut off. */
  #ots-page {
    box-sizing: border-box;
    width: 210mm;
    height: 296mm;
    padding: 15mm 16mm;
    overflow: hidden;

    /* The single knob the fitting script turns. Everything below is in em,
       so changing this scales the entire letter as one piece. */
    font-size: var(--ots-fs, 10.8pt);
    line-height: 1.6;
    font-family: "Nirmala UI", "Noto Sans Devanagari", "Mangal", "Sakal Marathi", system-ui, sans-serif;
    text-align: justify;
  }

  .ots-print-sheet { width: auto; }

  /* Uniform, em-based rhythm. The screen sheet's per-paragraph pixel margins
     went with the rest of its geometry, so spacing comes from here and
     scales with the type. */
  #ots-page p { margin: 0 0 0.5em 0; }

  /* The address block at the top and the sign-off at the foot are lists of
     short lines, not prose: they read wrong with paragraph gaps between them
     and justified spacing across them. */
  .ots-addr p, .ots-meta p, .ots-sign p { margin: 0 0 0.18em 0; text-align: left; }
  .ots-addr, .ots-meta { margin-bottom: 0.85em; }
  .ots-sign { margin-top: 1em; }
  .ots-sign .ots-sign-gap { margin-top: 0.9em; }

  /* The loans the application covers. */
  .ots-loans { margin: 0 0 0.6em 0; }
  .ots-loan { margin-bottom: 0.35em; text-align: left; }
  .ots-loan-amt { padding-left: 1.5em; }

  /* A filled-in value, with the rule under it that a branch expects. */
  .ots-filled { border-bottom: 1px dotted #555; padding: 0 2px; }

  /* Nothing splits, because nothing may leave the page. */
  #ots-page p, #ots-page div { break-inside: avoid; }
</style>
</head>
<body>
<div id="ots-page">${clone.outerHTML}</div>
<script>
(function () {
  var page = document.getElementById('ots-page');

  /* Step the one font-size down until the letter sits inside the page box.
     scrollHeight is the letter's real height even with overflow hidden, so
     comparing it with clientHeight is exactly "does this fit". 0.2pt steps
     from 10.8 bottom out at 7.6pt, which is small but legible -- and an
     application that long was unreadable spread over two pages anyway. */
  function fit() {
    var size = 10.8;
    var floor = 7.6;
    page.style.setProperty('--ots-fs', size + 'pt');
    var guard = 0;
    while (page.scrollHeight > page.clientHeight && size > floor && guard++ < 40) {
      size = Math.round((size - 0.2) * 10) / 10;
      page.style.setProperty('--ots-fs', size + 'pt');
    }
  }

  function go() {
    fit();
    /* A second pass one frame later: the first can run before the Devanagari
       font has actually been applied, and the letter is taller in a fallback
       font than in Nirmala UI. */
    requestAnimationFrame(function () {
      fit();
      try { window.focus(); window.print(); } catch (e) {}
    });
  }

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(go).catch(go);
  } else {
    window.setTimeout(go, 400);
  }
})();
<\/script>
</body>
</html>`);
      w.document.close();

      /* The child window fits itself and opens its own print dialogue, so
         there is nothing left to wait for here but the hand-off. */
      window.setTimeout(() => setBusy(false), 1200);
    } catch (err: any) {
      console.error('OTS application print failed:', err);
      setFailed('The application could not be opened for printing. Please try again.');
      setBusy(false);
    }
  };

  /**
   * NO WRAPPER COMPONENT HERE.
   *
   * A previous attempt at this bug replaced the inline field with a wrapper
   * that forwarded to the module-level one -- but the WRAPPER was still
   * created fresh on every render, so React still threw the input away on
   * every keystroke and focus was still lost after one character. The fix
   * only works if the component used in the markup is the same object every
   * time, so `Field` is used directly below.
   */

  const para = { margin: '0 0 10px 0', lineHeight: 1.85, textAlign: 'justify' as const };

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center bg-slate-900/70 backdrop-blur-xs p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-4 overflow-hidden flex flex-col max-h-[94vh]">
        <div className="px-5 py-3 bg-slate-900 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-bold">
              OTS अर्ज — {isToken ? 'आगाऊ रक्कम' : 'अंतिम तडजोड'}
            </h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white p-1 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900 shrink-0">
          हा अर्ज ग्राहकाकडून बँकेला आहे. यामध्ये एजंट, एजन्सी किंवा कंपनीचा कोणताही
          उल्लेख नाही. PDF कुठेही साठवली जात नाही — फक्त डाउनलोड होते.
        </div>

        {/* The application itself. This exact markup is what becomes the PDF. */}
        <div className="flex-1 overflow-y-auto bg-slate-100 p-3 sm:p-5">
          <div
            ref={sheetRef}
            style={{
              width: '190mm',
              maxWidth: '100%',
              margin: '0 auto',
              background: '#ffffff',
              padding: '14mm 12mm',
              color: '#000000',
              fontFamily:
                "'Nirmala UI', 'Noto Sans Devanagari', 'Mangal', 'Sakal Marathi', sans-serif",
              fontSize: '11.5pt',
              lineHeight: 1.85,
              boxSizing: 'border-box',
            }}
          >
            {/* The classes on these blocks are what the print stylesheet keys
                off. On screen they do nothing -- the inline styles below are
                still in charge here. On paper the inline styles are stripped
                and the classes take over, which is how the address lines stay
                tight and left-aligned while the body stays justified. */}
            <div className="ots-addr">
              <p style={{ margin: '0 0 6px 0' }}>प्रति,</p>
              <p style={{ margin: '0 0 2px 0' }}>मा. शाखा व्यवस्थापक,</p>
              <p style={{ margin: '0 0 2px 0' }}>
                <Field editing={editing} value={bankName} onChange={setBankName} w="60%" />,
              </p>
              <p style={{ margin: '0 0 2px 0' }}>
                <Field editing={editing} value={branch} onChange={setBranch} w="50%" />,
              </p>
              <p style={{ margin: '0 0 12px 0' }}>
                झोन – <Field editing={editing} value={zone} onChange={setZone} w="45%" />.
              </p>
            </div>

            <div className="ots-meta">
              <p style={{ margin: '0 0 8px 0' }}>
                दिनांक : <Field editing={editing} value={dateStr} onChange={setDateStr} w="35%" />
              </p>
              <p style={{ margin: '0 0 6px 0' }}>
                अर्जदाराचे नाव : <Field editing={editing} value={customerName} onChange={setCustomerName} w="60%" />
              </p>
              <p style={{ margin: '0 0 14px 0' }}>
                पत्ता : <Field editing={editing} value={address} onChange={setAddress} w="70%" />
              </p>
            </div>

            <p style={{ ...para, fontWeight: 700 }}>
              विषय :{' '}
              {isToken
                ? 'कर्ज खाते/खाती एकरकमी तडजोड योजनेअंतर्गत (OTS) निकाली काढण्याबाबत तडजोड प्रस्ताव.'
                : 'एकरकमी तडजोड योजनेअंतर्गत (OTS) अंतिम तडजोड रक्कम भरून कर्ज खाते/खाती निकाली काढण्याबाबत.'}
            </p>

            <p style={{ margin: '0 0 10px 0' }}>महोदय,</p>

            <p style={para}>मी आपल्या बँकेकडून खालीलप्रमाणे कर्ज घेतले आहे.</p>

            <div className="ots-loans" style={{ margin: '0 0 10px 0' }}>
              {loans.map((l, i) => (
                <div key={`loan-${i}`} className="ots-loan" style={{ marginBottom: '6px' }}>
                  <div>
                    {deva(i + 1)}. कर्ज खाते क्रमांक :{' '}
                    {editing ? (
                      <input
                        value={l.accountNo}
                        onChange={(e) =>
                          setLoans((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, accountNo: e.target.value } : x))
                          )
                        }
                        className="border-b border-dashed border-blue-400 bg-blue-50/40 px-1 outline-none"
                        style={{ width: '45%' }}
                      />
                    ) : (
                      l.accountNo
                    )}
                  </div>
                  <div className="ots-loan-amt" style={{ paddingLeft: '18px' }}>
                    कर्ज रक्कम : ₹
                    {editing ? (
                      <input
                        value={l.amount}
                        onChange={(e) =>
                          setLoans((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x))
                          )
                        }
                        className="border-b border-dashed border-blue-400 bg-blue-50/40 px-1 outline-none"
                        style={{ width: '35%' }}
                      />
                    ) : (
                      l.amount
                    )}
                    /-
                  </div>
                </div>
              ))}
            </div>

            <p style={{ ...para, fontWeight: 700 }}>
              एकूण कर्ज रक्कम : ₹{inr(totalLoan)}/-
            </p>

            {isToken ? (
              <>
                <p style={para}>
                  सध्या आर्थिक अडचणीमुळे नियमितपणे कर्जाची परतफेड करणे मला शक्य झाले नाही.
                  त्यामुळे वरील कर्ज खाते/खाती एकरकमी तडजोड योजना (OTS) अंतर्गत निकाली
                  काढण्याची माझी इच्छा आहे.
                </p>
                <p style={para}>
                  मी सदर कर्ज खाते/खात्यांसाठी एकूण तडजोड रक्कम ₹
                  <Field editing={editing} value={otsAmount} onChange={setOtsAmount} w="22%" />
                  /- मंजूर करण्याची विनंती करीत आहे.
                </p>
                <p style={para}>
                  सदर तडजोड प्रस्तावाच्या अनुषंगाने एकूण तडजोड रकमेच्या आगाऊ रकमेपोटी ₹
                  <Field editing={editing} value={paid} onChange={setPaid} w="22%" />
                  /- आज दिनांक {dateStr} रोजी बँकेत जमा करीत आहे.
                </p>
                <p style={para}>
                  उर्वरित ₹{inr(remaining)}/- रक्कम ही बँकेकडून तडजोड प्रस्तावास मंजुरी
                  मिळाल्यानंतर, बँकेच्या मंजुरीपत्रामध्ये नमूद केलेल्या अटी व मुदतीनुसार भरण्यास मी
                  पूर्णतः तयार आहे.
                </p>
                <p style={para}>
                  तडजोड प्रस्तावास मंजुरी मिळाल्यानंतर बँकेच्या सर्व अटी व शर्ती मला मान्य
                  राहतील. निर्धारित मुदतीत मंजूर तडजोड रक्कम न भरल्यास सदर तडजोड प्रस्ताव रद्द
                  होऊ शकतो, याची मला पूर्ण जाणीव आहे.
                </p>
                <p style={para}>
                  त्यामुळे माझ्या अर्जाचा सहानुभूतीपूर्वक विचार करून वरील कर्ज खाते/खाती एकरकमी
                  तडजोड योजना (OTS) अंतर्गत मंजूर करून द्यावीत, ही नम्र विनंती.
                </p>
              </>
            ) : (
              <>
                <p style={para}>
                  वरील कर्ज खाते/खाती एकरकमी तडजोड योजना (OTS) अंतर्गत निकाली काढण्यासाठी
                  निश्चित करण्यात आलेली एकूण तडजोड रक्कम ₹
                  <Field editing={editing} value={otsAmount} onChange={setOtsAmount} w="22%" />
                  /- आहे.
                </p>
                <p style={para}>
                  सदर तडजोड योजनेअंतर्गत आज दिनांक {dateStr} रोजी अंतिम तडजोड रक्कम म्हणून ₹
                  <Field editing={editing} value={paid} onChange={setPaid} w="22%" />
                  /- बँकेत जमा करीत आहे.
                </p>
                <p style={para}>
                  सदर रक्कम ही एकरकमी तडजोड योजनेअंतर्गत अंतिम तडजोड रक्कम असून, संबंधित कर्ज
                  खाते/खाती एकरकमी तडजोड योजनेअंतर्गत निकाली काढून आवश्यक ती कार्यवाही पूर्ण
                  करावी, ही विनंती आहे.
                </p>
                <p style={para}>
                  म्हणून सदर अंतिम तडजोड रक्कम स्वीकारून, लागू नियम व अटींनुसार संबंधित कर्ज
                  खाते/खाती एकरकमी तडजोड योजनेअंतर्गत निकाली काढून खाते बंद करण्याची आवश्यक
                  कार्यवाही करावी, ही नम्र विनंती.
                </p>
              </>
            )}

            <div className="ots-sign">
              <p style={{ margin: '14px 0 10px 0' }}>धन्यवाद.</p>
              <p style={{ margin: '0 0 22px 0' }}>आपला/आपली विश्वासू,</p>
              <p className="ots-sign-gap" style={{ margin: '0 0 14px 0' }}>
                अर्जदाराची स्वाक्षरी : ______________________
              </p>
              <p style={{ margin: 0 }}>अर्जदाराचे नाव : {customerName || '—'}</p>
            </div>
          </div>
        </div>

        {failed && (
          <div className="px-5 py-2 bg-rose-50 border-t border-rose-200 text-[11px] text-rose-800 flex items-center gap-1.5 shrink-0">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <span>{failed}</span>
          </div>
        )}

        <div className="px-5 py-3 border-t border-slate-200 flex flex-wrap items-center justify-end gap-2 shrink-0 bg-white">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-xs transition"
          >
            रद्द करा
          </button>

          <button
            type="button"
            onClick={() => setEditing((v) => !v)}
            className={`px-4 py-2 rounded-xl font-bold text-xs transition flex items-center gap-1.5 border ${
              editing
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50'
            }`}
          >
            <Pencil className="w-3.5 h-3.5" />
            <span>{editing ? 'संपादन पूर्ण' : 'अर्ज संपादित करा'}</span>
          </button>

          {/* The separate Print button has gone: the main button now opens
              the print dialogue itself, where "Save as PDF" produces the A4
              file. Two buttons doing the same thing was only ever a
              consequence of the rasteriser failing so often. */}

          <button
            type="button"
            onClick={download}
            disabled={busy}
            className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded-xl font-bold text-xs flex items-center gap-1.5 shadow-sm transition"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            <span>{busy ? 'तयार करत आहे…' : 'अर्जाची पुष्टी करा'}</span>
            {!busy && <Download className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OTSApplicationModal;
